<template>
    <div>
        <div class="input p-l-sm p-t-smd p-b-smd set-auto-height" :class="hasError ? 'is-danger' : ''">
            <e-taglist>
                <e-tag
                    v-for="item in selectedCourses"
                    :key="item.id"
                    size="is-small"
                    closable
                    aria-close-label="Close tag"
                    @close="remove(item)"
                >
                    {{ item.title }}
                </e-tag>

                <a v-if="alreadySelected.length < limit && !disabled" @click="openModal = true">
                    <e-icon icon="plus-circle" />
                </a>
                <a v-else class="disabled">
                    <e-icon icon="plus-circle" />
                </a>
            </e-taglist>
        </div>
        <modal-select
            ref="modalSelect"
            tag-type="course"
            label="course"
            :open="openModal"
            :limit="limit"
            :filters="filters"
            :sorts="sorts"
            :endpoint="endpoint"
            :available-view="['detail']"
            :additional-filter="additionalFilter"
            :already-selected="alreadySelected"
            :additional-include="['groups', 'tags']"
            base-view="detail"
            @chosen="save"
            @close="close"
        >
            <template #detail-view="{ items, selected, selectItem }">
                <div class="column is-full is-narrow has-text-left">
                    <e-table
                        ref="table"
                        :data="items"
                        :checked-rows="selected"
                        checkable
                        :focusable="limit === 1"
                        :header-checkable="limit === 0"
                        backend-sorting
                        @check="(selected, row) => selectItem(row)"
                        @select="selectItem"
                    >
                        <e-table-column v-slot="{ row: course }" ref="title" field="title" label="Title" sortable>
                            {{ course.title }}
                        </e-table-column>

                        <e-table-column v-slot="{ row: course }" field="tags" label="Tags">
                            <e-taglist>
                                <e-tag v-for="tag in get(course, 'tags')" :key="tag.id">
                                    {{ tag.name.en }}
                                </e-tag>
                            </e-taglist>
                        </e-table-column>

                        <e-table-column v-slot="{ row: course }" field="groups" label="Groups">
                            <e-taglist>
                                <e-tag v-for="group in get(course, 'groups')" :key="group.id">
                                    {{ group.name }}
                                </e-tag>
                            </e-taglist>
                        </e-table-column>
                    </e-table>
                </div>
            </template>
        </modal-select>
    </div>
</template>
<script>
import get from 'lodash/get.js';

import ModalSelect from '@/components/modal-select/base/ModalSelect.vue';
import GroupFilter from '@/components/modal-select/filters/GroupFilter.js';
import TagFilter from '@/components/modal-select/filters/TagFilter.js';
import TextFilter from '@/components/modal-select/filters/TextFilter.js';
import QueryParamSyncForModalSelectMixin from '@/mixins/QueryParamSyncForModalSelectMixin.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    name: 'CourseSelect',
    components: { ModalSelect },
    mixins: [QueryParamSyncForModalSelectMixin],

    props: {
        open: {
            type: Boolean,
            required: false,
            default: false,
        },

        label: {
            type: String,
            required: false,
            default: 'Courses',
        },

        alreadySelected: {
            type: Array,
            required: false,
            default: () => [],
        },

        limit: {
            type: Number,
            required: false,
            default: 10,
        },

        hasError: {
            type: Boolean,
            required: false,
            default: false,
        },

        additionalFilter: {
            type: Object,
            required: false,
            default: null,
        },

        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },

        enableQueryParams: {
            type: Boolean,
            required: false,
            default: false,
        },

        urlParam: {
            type: String,
            required: false,
            default: '',
        },

        endpoint: {
            type: String,
            required: false,
            default: 'courses',
        },
    },

    emits: ['save', 'remove', 'close'],

    data: () => ({
        openModal: false,
        filters: [new TextFilter('title'), new GroupFilter('groups'), new TagFilter('tags')],
        sorts: ['title'],
        selectedFromQuery: null,
    }),

    computed: {
        selectedCourses() {
            return this.alreadySelected.length > 0 ? this.alreadySelected : this.selectedFromQuery;
        },
    },

    watch: {
        open(open) {
            this.openModal = open;
        },
    },

    created() {
        if (this.enableQueryParams) {
            this.fillFromQueryParams();
        }
    },

    beforeUnmount() {
        this.openModal = false;
    },

    methods: {
        get,

        save(chosen) {
            if (this.enableQueryParams) {
                this.setQueryParams(chosen);
            }
            this.$emit('save', chosen);
        },

        close() {
            this.openModal = false;

            this.$emit('close');
        },

        remove(chosen) {
            this.$emit('remove', chosen);
            this.selectedFromQuery = [];
            this.removeQueryParams();
        },

        setQueryParams(chosen) {
            const url = new URL(window.location.href);
            url.searchParams.set(this.urlParam, chosen.map(item => item.id).join(','));
            window.history.pushState({}, ',', url);
        },

        removeQueryParams() {
            const url = new URL(window.location.href);
            url.searchParams.delete(this.urlParam);
            window.history.pushState({}, ',', url);
        },

        fillFromQueryParams() {
            const url = new URL(window.location.href);
            const ids = url.searchParams.get(this.urlParam);

            if (ids) {
                const queryBuilder = new QueryBuilder('courses');
                queryBuilder.filter('id', ids.split(','));

                axios.get(queryBuilder.toUrl()).then(response => {
                    this.selectedFromQuery = response.data.data;
                    this.$emit('save', response.data.data);
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.set-auto-height {
    height: auto !important;
}

.disabled {
    pointer-events: none;
    color: darkgrey;
}
</style>
